import { useEffect, useState } from "react";
import './style.scss';
import Helmet from "react-helmet";
import { PasswordField } from "../../components/forms";
import archiver from "../../api/Archiver";
import InstanceTarget from "../../types/instance/InstanceTarget";
import { EXPIRATION_DAYS, EXPIRATION_KEY, messages, REDIRECT_PAGE, SSO_PROVIDER_KEY, TOKEN_KEY } from "../../component/Constants";

// SSO Provider interface
interface SSOProvider {
    name: string;
    displayName: string;
}

function random(): number {
    return process.env.NODE_ENV === 'test' ? 0 : Math.random();
}

function Login(props: { loginError?: string }) {
    const [userName, setUserName] = useState<string>();
    const [password, setPassword] = useState<string>();
    const [message, setMessage] = useState<string>(messages[Math.floor(random() * messages.length)]);
    const [loading, setLoading] = useState<boolean>(false);
    const [checkingAuth, setCheckingAuth] = useState(false);
    const [ssoProviders, setSSOProviders] = useState<SSOProvider[]>([]);
    const [ssoEnabled, setSSOEnabled] = useState<boolean>(false);
    const [error, setError] = useState<string>(props.loginError || '');

    useEffect(() => {
        async function checkLogin() {
			// Load SSO configuration
			//console.log('Loading SSO configuration...');
            const urlParams = new URLSearchParams(window.location.search);
			const errorParam = urlParams.get ('error');
			const code = urlParams.get ('code');
			const state = urlParams.get ('state');
			const token = urlParams.get ('token');

			try
			{
				setCheckingAuth (true);
				const ssoResponse = await archiver.get ('/oauth2/providers');
				setSSOEnabled (ssoResponse.data.enabled);
				setSSOProviders (JSON.parse (ssoResponse.data.providers || '[]'));
				setCheckingAuth (false);
				//console.log('SSO configuration loaded:', ssoResponse.data);
			}
			catch (error)
			{
				console.warn ('Failed to load SSO configuration:', error);
				setError ('Failed to load SSO configuration');
				setCheckingAuth (false);
			}

            //console.log('Starting login check...');
			if (errorParam)
			{
				setError (errorParam);
				console.error (error, urlParams.get ('error_description'));
				setCheckingAuth (false);
			}
			else if (code && state)
			{
				// Handle OAuth2 callback - forward to backend
                //console.log('OAuth2 callback detected, forwarding to backend...');
                // Forward the callback to the backend
				setCheckingAuth (true);
                const callbackData = [{ code, state }];
                archiver.post('/oauth2/callback', callbackData)
                    .then((response: any) => {
                        //console.log('OAuth2 callback response:', response.data);
                        let redirectUrl = null;

                        // Handle different response structures
                        if (response.data && response.data.length > 0 && response.data[0].redirectUrl) {
                            redirectUrl = response.data[0].redirectUrl;
                        } else if (response.data && response.data.redirectUrl) {
                            redirectUrl = response.data.redirectUrl;
                        }

                        if (redirectUrl) {
                            //console.log('Redirecting to:', redirectUrl);
                            window.location.href = redirectUrl;
                        } else {
                            //console.log('SSO callback failed - no redirectUrl found');
                            //console.log('Response structure:', JSON.stringify(response.data, null, 2));
                            setError('SSO callback failed');
                            setCheckingAuth(false);
                        }
                    })
                    .catch((error: any) => {
                        console.error('SSO callback error:', error);
                        setError('SSO authentication failed');
                        setCheckingAuth(false);
                    });
            }
			else if (token && token.startsWith ('c3Nv'))
			{
				// First, check for SSO token in URL parameters and process it
                //console.log('SSO token found in URL, processing...');
                // Set SSO token
				setCheckingAuth (true);
                const expirationDate = new Date();
				expirationDate.setDate (expirationDate.getDate () + EXPIRATION_DAYS);
                window.localStorage.setItem(TOKEN_KEY, token);
                window.localStorage.setItem(EXPIRATION_KEY, expirationDate.toISOString());
				archiver.post ('/rest/v1/login/name', [])
				.then ((/*response*/) =>
				{
					//console.log ('SSO login successful, redirecting to home');
					// Redirect to home
					const redirectUrl = window.localStorage.getItem (REDIRECT_PAGE);
					window.history.replaceState (null, '', redirectUrl || '/');
					window.location.replace (redirectUrl || '/');
				})
				.catch ((x: any) =>
				{
					console.error ('error', x);
					setError ('500');
					setCheckingAuth (false);
				});
            }
        }

        // Run authentication check immediately
        checkLogin();
    }, []);

    useEffect(() => {

        let timeout: NodeJS.Timeout;

        timeout = setInterval(() => setMessage(messages[Math.floor(random() * messages.length)]), 9000);

        return () => {
            clearTimeout(timeout);
        };
    }, []);

    const handleLogin = async () => {
        setLoading(true)
        // Prevent the Axios interceptor using the old token.
        window.localStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(EXPIRATION_KEY);
        window.localStorage.removeItem(SSO_PROVIDER_KEY);
        const token = btoa(`${userName}:${password}`);
        const url = '/auth.jsp';
        archiver<InstanceTarget[]>({ url, headers: { Authorization: 'Basic ' + token } })
                .then((response) => {
                    if (response && Array.isArray(response.data)) {
                        const redirectUrl = window.localStorage.getItem(REDIRECT_PAGE);
                        const expirationDate = new Date();
                        expirationDate.setDate (expirationDate.getDate () + EXPIRATION_DAYS);
                        window.localStorage.setItem(TOKEN_KEY, token);
                        window.localStorage.setItem(EXPIRATION_KEY, expirationDate.toISOString());
                        window.localStorage.removeItem(REDIRECT_PAGE);
                        archiver.post('/rest/v1/login/name', [])
                                .then((/*response*/) => {
                                    window.history.replaceState(null, '', redirectUrl || '/');
                                    window.location.replace(redirectUrl || '/');
                                })
                                .catch((x: any) => {
                                    console.error('error', x);
                                    setLoading(false)
                                    setError('500');
                                });
                    } else {
                        console.error('error', response);
                        setLoading(false)
                        setError('500');
                    }
                })
                .catch((x: any) => {
                    let errorName = '401';

                    if (!x.response) {
                        console.error('error', x);
                        errorName = '500';
                    } else if (x.response.status !== 401) {
                        console.error('error', x);
                        errorName = x.response.statusText;
                    }

                    setLoading(false);
                    setError(errorName);
                });
    };

    const handleSSOLogin = async (providerName: string) => {
        setCheckingAuth(true);
        window.localStorage.removeItem(TOKEN_KEY);
        window.localStorage.removeItem(EXPIRATION_KEY);
        window.localStorage.setItem(SSO_PROVIDER_KEY, providerName);
        try {
            const response = await archiver.get(`/oauth2/initiate?provider=${providerName}`);
            if (response.data.authUrl) {
                // Redirect to SSO provider
                window.location.href = response.data.authUrl;
            } else {
                setError('SSO configuration error');
                setCheckingAuth(false);
            }
        } catch (error: any) {
            console.error('SSO login error:', error);
            setError('Failed to initiate SSO login');
            setCheckingAuth(false);
        }
    };

    const getErrorText = (value: string) => {
        const messages: { [key: string]: string } = {
            '401': 'Invalid Credentials',
            '500': '500 Internal Server Error',
            '502': '502 Bad Gateway',
            '503': '503 Service Unavailable',
            '504': '504 Gateway Timeout',
            expired: 'Session expired, please log in'
        };

        return messages[value] || value;
    };

    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter') {
            void handleLogin();
        }
    };
    if (checkingAuth) {
        return (
                <div className="dbmar-page-wrapper">
                    <div className="dbmar-page text-center">
                        <div className="spinner">
                            <img src="/icons/icon.png" className="fish" width="100" height="100" alt="Loading"/>
                        </div>
                        <p className="text-muted">Checking login status...</p>
                    </div>
                </div>
        );
    }

    return (
            <div className='dbmar-page-wrapper'>
                <div className='dbmar-page'>
                    <Helmet>
                        <title>DBmarlin Log In</title>
                        <meta name="description" content="Log In"/>
                    </Helmet>

                    <div className="card">
                        <div className="card-text-header">
                            <div className='text-center'>
                                <img src="/icons/dbmarlin-badge.svg" alt="DBmarlin" className="logo-large" width="200px"
                                     height="70px"/>
                            </div>
                            <p className='login-header'>Log In to DBmarlin</p>
                        </div>

                        <div className="col">
                            {ssoEnabled && ssoProviders.length > 0 && (
                                <div className='m-2'>
                                    <p className='text-center mb-3'>Sign in with:</p>
                                    <div className='d-grid gap-2 mb-3'>
                                        {ssoProviders.map((provider) => (
                                            <button
                                                key={provider.name}
                                                onClick={() => handleSSOLogin(provider.name)}
                                                type='button'
                                                disabled={loading}
                                                className='btn btn-outline-primary'
                                            >
                                                <i className="fa-solid fa-sign-in-alt me-2"></i>
                                                {provider.displayName}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="text-center mb-3">
                                        <span className="text-muted">or</span>
                                    </div>
                                </div>
                            )}

                            <form autoComplete="on" onSubmit={(e) => { e.preventDefault(); void handleLogin(); }}>
                                <div className='m-2'>
                                    <input
                                            type="text"
                                            id="userName"
                                            name="username"
                                            placeholder='Username'
                                            className="form-control"
                                            onChange={(e) => setUserName(e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            autoComplete="username"
                                    />
                                </div>

                                <div className='m-2'>
                                    <PasswordField
                                        id="password"
                                        name="password"
                                        placeholder="Password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        standalone
                                        autoComplete="current-password"
                                    />
                                </div>
                                <p className='login-error'>{getErrorText(error)}</p>

                                <div className='m-2'>
                                    <button type='submit'
                                            disabled={!userName?.trim() || !password?.trim() || loading}
                                            className={`btn btn-primary w-100`}>
                                        Login
                                    </button>
                                </div>
                            </form>
                            <p className='login-link'><a
                                    href='https://docs.dbmarlin.com/docs/Getting-Started/access-control/authentication#update-user-change-password'>Lost
                                your password?</a></p>
                            <p className='login-link'><a
                                    href='https://docs.dbmarlin.com/docs/Getting-Started/access-control/authentication#add-user'>Add
                                new Account</a></p>
                        </div>
                    </div>

                    <div className="initialising page-animation">
                        <div className="w-100 text-center text-muted">
                            <div className="spinner"><img src="/icons/icon.png" className="fish" width="100"
                                                          height="100"
                                                          alt="DBmarlin"/></div>
                        </div>
                    </div>
                    <div className='invisible-wave'></div>
                    <div className="page-marlin-facts">
                        <div className="waves"></div>
                        <div className="message">{message}</div>
                        <p className="copyright">&#169;dbmarlin.com</p>
                    </div>
                </div>
            </div>
    )
}

export default Login;
